/**
 * Mark Norris — public-facing My Story content.
 * Drawn from spec §5; deliberately omits §5.1 sensitive material
 * (tumour dimensions, dosing, other patients, personal incidence stats).
 */

export const storyMeta = {
  title: 'My Story',
  documentTitle: "Mark's Story · NextOnVera",
  eyebrow: "Mark Norris's story",
  name: 'Mark Norris',
  role: 'Founder, NextOnVera',
  bioLine:
    'Senior technology executive · 20+ years guiding large organisations · Melbourne',
  portraitSrc: '/generated-images/mark-portrait.jpg',
  portraitAlt: 'Mark Norris at home on the deck with his dog'
};

/** Spec §5 public-facing pull quotes (safe to publish). */
export const pullQuotes = {
  cioPatient: 'A CIO who became a patient, and a patient who refused to stop building.',
  whyMe: "I don't waste time on 'why me.' The question is pointless.",
  loveStory:
    "If someone had done five years ago what we are doing today, I might have more options now. We have the opportunity to change that for the people who come after us.",
  refuseTake:
    'The disease has taken from me, and it will take again. But I will not let it take the life I still have, or the people who make that life worth living.',
  peopleAfter:
    "Everything we're building is aimed at changing that for the people diagnosed after me."
} as const;

export type StoryBeat = {
  id: string;
  title: string;
  /** Optional mid-section pull quote from the public set */
  pullQuote?: string;
  paragraphs: string[];
};

/**
 * Four narrative beats in first person.
 * No private clinical detail, no other-patient stories, no personal prognosis numbers.
 * MGMT-unmethylated disclosure is explicit in public structure §5 (not a §5.1 exclusion).
 */
export const storyBeats: StoryBeat[] = [
  {
    id: 'the-diagnosis',
    title: 'The diagnosis',
    pullQuote: pullQuotes.whyMe,
    paragraphs: [
      'For a stretch of time the headaches were easy to file away — stress, screens, long days. I was a CIO. I ran technology for large businesses. Pain was something you worked through.',
      'On Boxing Day at Chadstone, that calculation broke. My wife Lenice saw what I was still trying to manage, and she insisted we go to the emergency room. She was right.',
      'The MRI said enough. That night I was in emergency surgery at The Alfred. I went from leading corporate technology agendas to a patient on a table in a matter of hours — a CIO becoming, suddenly, the person the system was built to care for.',
      "I was 52. No warning signs. No family history. A week later, I lost spatial awareness so completely that I couldn’t figure out how to get into my own car. There was no more pretending this was manageable."
    ]
  },
  {
    id: 'the-fight',
    title: 'The fight',
    pullQuote: pullQuotes.refuseTake,
    paragraphs: [
      'The operation ran more than eight hours. The surgical team removed over 95% of the tumour. When I woke, Lenice and our sons, Ethan and Joshua, were there. That is the part I return to.',
      'The treatment plan was direct: forty-two consecutive days of radiation and chemotherapy, then six months of continued chemotherapy. The work of recovery was quieter than the drama of diagnosis — walking every day, treating sleep as the best medicine, accepting help I never wanted to need, and learning to live one deliberate day at a time.',
      'This is still a fight. It has taken from me, and I will not pretend otherwise. But the people who keep showing up — Lenice, Ethan, Joshua — carry more weight than this illness ever will. That is what gets me through the days that are hard.'
    ]
  },
  {
    id: 'why-building',
    title: "Why I'm building this",
    pullQuote: pullQuotes.cioPatient,
    paragraphs: [
      "My professional life has spanned more than twenty years leading technology across large Australian organisations — CIO roles including Total Tools, Asahi Beverages, and currently Greencross. I'm a GAICD graduate and the founder of JELMM Consulting. Systems thinking is how I see the world.",
      "When I learned that my own tumour's genetic profile — MGMT-unmethylated — means the standard chemotherapy drug is largely ineffective for me personally, something shifted. It wasn't abstract policy. It was my life, and the same gap faces thousands of Australians diagnosed after me.",
      'That is what drove me to start NextOnVera: clinical intelligence that finally treats each brain cancer for the molecular disease it is. Through NextOnVera — led by Tour De Cure — we are funding Cancer Navigator for patients and carers, and Grace AI for everyday wellbeing, alongside that research engine. I can no longer separate the executive who builds systems from the patient who needs better ones.',
      "This programme won’t change my outcome — the timelines don’t work in my favour. But I should have more options. Every patient should. And the only way those options arrive is if someone funds the work that’s too early for everyone else.",
      "I’ve met researchers with promising work who’ve stopped applying for funding because they’re repeatedly rejected. The feedback is always the same: unclear ROI. That is exactly the gap this fund exists to close."
    ]
  },
  {
    id: 'the-mission',
    title: 'The mission, in my words',
    pullQuote: pullQuotes.peopleAfter,
    paragraphs: [
      "Brain cancer survival hasn't meaningfully moved in a generation. Not for lack of brilliant clinicians, and not for lack of people who care. The system that funds early insight and turns it into evidence has not kept pace with how complex this disease actually is.",
      "Globally, there are no documented cases of patients beating glioblastoma. Not managing it long-term. Not curing it. Zero. That fact should anger every person who reads it — it certainly angers me.",
      "NextOnVera exists to close that gap — technology-led research, patient support that respects real lives, and a coalition model that lets partners act together. Everything we're building is aimed at the people diagnosed after me. That is the only definition of success I will accept.",
      'I live in Melbourne with Lenice, Ethan, and Joshua. They are the reason I am still here, and the standard I measure this work against. If you are reading this as a patient, a carer, a clinician, or a partner who wants to help — you are already part of the next chapter.'
    ]
  }
];

export const storyClosingCta = {
  eyebrow: 'What happens next',
  heading:
    "Mark's story is the reason this exists. Here's how you can be part of what happens next.",
  body: 'Support the Brain Cancer Fund, partner with us to back NextOnVera and early-stage research, or lend your voice to advocacy. NextOnVera is led by Tour De Cure — and it is built for the people who come after.',
  primary: {
    label: 'Support the Fund',
    href: '/the-fund'
  },
  secondary: {
    label: 'Get involved',
    href: '/get-involved'
  }
};
